// "Who has been changing our agent configuration" â€” the activity feed (ADR-016).
//
// Deliberately separate from the findings inbox, and deliberately shows changes
// that raised nothing at all. Answering "who edits this" only from the findings
// table would show the scary half and imply nobody else touches these files.

import { Link } from "@/components/ui/link";
import * as React from "react";

import { Actor, SeverityBadge, Sha, When } from "@/components/governance/bits";
import { GovernanceHeader } from "@/components/governance/nav";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { useGovernanceActivity, useGovernedRepos } from "@/lib/query/hooks";

export default function GovernanceActivityPage() {
  const [actorInput, setActorInput] = React.useState("");
  const [actor, setActor] = React.useState<string | undefined>(undefined);
  const [repoId, setRepoId] = React.useState<number | undefined>(undefined);

  // No refresh from GitHub here â€” this page only needs repo names for the
  // filter, and a filter dropdown is not worth an API round-trip to GitHub.
  const repos = useGovernedRepos(false);
  const activity = useGovernanceActivity({ actor, repoId });

  return (
    <div className="space-y-6">
      <GovernanceHeader description="Every change to a governed AI agent file, whether or not anything was flagged â€” who made it, where, and in which pull request." />

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={repoId ?? ""}
          onChange={(e) => setRepoId(e.target.value ? Number(e.target.value) : undefined)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All repositories</option>
          {(repos.data ?? [])
            .filter((r) => r.enabled)
            .map((repo) => (
              <option key={repo.github_repo_id} value={repo.github_repo_id}>
                {repo.full_name}
              </option>
            ))}
        </select>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setActor(actorInput.trim() || undefined);
          }}
        >
          <Input
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            placeholder="Filter by GitHub loginâ€¦"
            className="h-9 w-56"
          />
          <Button size="sm" variant="outline" type="submit">
            Filter
          </Button>
          {actor ? (
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => {
                setActorInput("");
                setActor(undefined);
              }}
            >
              Clear
            </Button>
          ) : null}
        </form>
      </div>

      {activity.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : activity.isError ? (
        <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
      ) : (activity.data ?? []).length === 0 ? (
        <EmptyState
          title="No agent-config changes recorded yet"
          hint="Entries appear here as soon as a pull request touches a governed file in an enabled repository."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Risk</TH>
                  <TH>File</TH>
                  <TH>Change</TH>
                  <TH>Repository</TH>
                  <TH>Changed by</TH>
                  <TH>PR</TH>
                  <TH>When</TH>
                </TR>
              </THead>
              <TBody>
                {(activity.data ?? []).map((event) => (
                  <TR key={event.id}>
                    <TD>
                      <SeverityBadge severity={event.max_severity} />
                    </TD>
                    <TD className="max-w-xs">
                      <code className="block truncate text-xs">{event.path}</code>
                      <span className="text-xs text-muted-foreground">
                        {event.kind.replace(/_/g, " ")}
                      </span>
                    </TD>
                    <TD>
                      <Badge variant="outline">{event.change_type}</Badge>
                    </TD>
                    <TD className="text-sm">{event.repo_full_name}</TD>
                    <TD>
                      <div className="flex flex-col gap-0.5">
                        <Actor login={event.actor_login} />
                        <Sha sha={event.commit_sha} />
                      </div>
                    </TD>
                    <TD className="text-sm">
                      {event.pr_review_id ? (
                        <Link
                          href={`/governance/${event.pr_review_id}`}
                          className="underline underline-offset-4"
                        >
                          #{event.pr_number}
                        </Link>
                      ) : (
                        `#${event.pr_number ?? "â€”"}`
                      )}
                    </TD>
                    <TD>
                      <When iso={event.occurred_at} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
